import type { Comp, FormattedText, GameObj } from "kaplay";
import { LOOKS, type LookKey } from "../config/assetManifest";
import { k } from "../core/kaplay";

export interface LookComp extends Comp {
    key: LookKey;
    pose: string;
    flipX: boolean;
    offsetY: number;
    spriteScale?: InstanceType<typeof k.Vec2>;
    renderArea(): InstanceType<typeof k.Rect>;
}

const OUTLINE = k.rgb(40, 32, 28);

export const lookLabels = { visible: false };

export function look(key: LookKey, pose?: string): LookComp {
    const def = LOOKS[key];
    const fill = k.Color.fromHex(def.color);
    let label: FormattedText | null = null;
    let labelFor = "";

    const labelText = (pose: string, pos: InstanceType<typeof k.Vec2>) => {
        const cacheKey = `${pose}|${pos.x}|${pos.y}`;
        if (!label || labelFor !== cacheKey) {
            label = k.formatText({
                text: pose ? `${key}\n${pose}` : key,
                size: 14,
                pos,
                anchor: "center",
                align: "center",
                color: OUTLINE,
            });
            labelFor = cacheKey;
        }
        return label;
    };

    return {
        id: "look",
        key,
        pose: pose ?? def.poses?.[0] ?? "",
        flipX: false,
        offsetY: 0,

        renderArea() {
            return new k.Rect(k.vec2(-def.w / 2, -def.h), def.w, def.h);
        },

        draw(this: GameObj<LookComp>) {
            const centerY = this.offsetY - def.h / 2;
            const deg = (this as any).angle ?? 0;
            const rad = (deg * Math.PI) / 180;
            const pos = k.vec2(Math.sin(rad) * centerY, Math.cos(rad) * centerY);
            const scale = this.spriteScale ?? k.vec2(1, 1);

            if (k.getSprite(key)?.loaded) {
                const frame = Math.max(0, def.poses?.indexOf(this.pose) ?? 0);
                k.drawSprite({
                    sprite: key,
                    frame,
                    pos,
                    anchor: "center",
                    scale,
                    flipX: this.flipX,
                });
                return;
            }
            k.drawRect({
                width: def.w * Math.abs(scale.x),
                height: def.h * Math.abs(scale.y),
                pos,
                anchor: "center",
                color: fill,
                radius: 8,
                outline: { width: 4, color: OUTLINE },
            });
            if (lookLabels.visible) k.drawFormattedText(labelText(this.pose, pos));
        },
    };
}
